import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchClientHealth, fetchClientsLite, fetchMorningCheck, fetchSession } from "../api";
import type { MorningCheckPayload, SessionPayload } from "../types";

type ClientHealthLoadState = "idle" | "loading" | "ready" | "error";

type DashboardContextValue = {
  data: MorningCheckPayload | null;
  session: SessionPayload | null;
  selectedClient: string;
  setSelectedClient: (slug: string) => void;
  loading: boolean;
  /** La sesión ya llegó: las rutas pueden abrirse sin esperar la lista de clientes ni el Morning Check. */
  sessionReady: boolean;
  selectedClientHealthState: ClientHealthLoadState;
  error: string | null;
  refresh: () => Promise<void>;
};

const DashboardContext = createContext<DashboardContextValue | null>(null);

const STORAGE_KEY = "wolf.selectedClient";

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<MorningCheckPayload | null>(null);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [selectedClient, setSelectedClientState] = useState(
    () => localStorage.getItem(STORAGE_KEY) ?? "ALL",
  );
  const [loading, setLoading] = useState(true);
  const [healthLoadStates, setHealthLoadStates] = useState<
    Record<string, "loading" | "ready" | "error">
  >({});
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const nextSession = await fetchSession();
      setSession(nextSession);
      const isClientScoped = Boolean(nextSession.isClientUser || nextSession.isClientPortal);

      let nextData: MorningCheckPayload;
      if (isClientScoped) {
        const tenant = nextSession.tenantSlug || "cliente";
        const now = new Date().toISOString();
        nextData = {
          generatedAt: now,
          dataAsOf: now,
          headline: "Portal de cliente",
          portfolio: {
            spendMtd: 0,
            activeClients: 1,
            over: 0,
            atRisk: 0,
            criticalAlerts: 0,
            recoverable: 0,
            recoverableClients: 0,
          },
          clients: [
            {
              clientSlug: tenant,
              displayName: tenant.toUpperCase(),
              spendMtd: 0,
              spendDailyMtd: 0,
              budgetMonthEst: 0,
              pacingPct: 0,
              status: "OK",
              alertCount: 0,
              criticalCount: 0,
            },
          ],
          alerts: [],
          health: {},
        };
      } else if (nextSession.modulos?.includes("morning_check")) {
        nextData = await fetchMorningCheck();
      } else {
        // Sin el módulo Morning Check solo se carga la lista de clientes para el selector.
        const lista = await fetchClientsLite();
        const now = new Date().toISOString();
        nextData = {
          generatedAt: now,
          dataAsOf: now,
          headline: "",
          portfolio: { spendMtd: 0, activeClients: lista.length, over: 0, atRisk: 0, criticalAlerts: 0, recoverable: 0, recoverableClients: 0 },
          clients: lista.map((c) => ({ clientSlug: c.clientSlug, displayName: c.displayName, objective: c.objective, spendMtd: 0, spendDailyMtd: 0, budgetMonthEst: 0, pacingPct: 0, status: "OK" as const, alertCount: 0, criticalCount: 0 })),
          alerts: [],
          health: {},
        };
      }

      setHealthLoadStates({});
      setData(nextData);

      const forcedTenant = nextSession.tenantSlug;
      if (forcedTenant && isClientScoped) {
        setSelectedClientState(forcedTenant);
        localStorage.setItem(STORAGE_KEY, forcedTenant);
      } else if (
        selectedClient !== "ALL" &&
        !nextData.clients.some((client) => client.clientSlug === selectedClient)
      ) {
        setSelectedClientState("ALL");
        localStorage.setItem(STORAGE_KEY, "ALL");
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible cargar el tablero.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.location.pathname === "/login" || window.location.pathname === "/invitacion") {
      setLoading(false);
      return;
    }
    void refresh();
  }, []);

  useEffect(() => {
    if (
      !data ||
      selectedClient === "ALL" ||
      session?.isClientUser ||
      session?.isClientPortal ||
      data.health[selectedClient] ||
      healthLoadStates[selectedClient]
    ) return;
    setHealthLoadStates((current) => ({ ...current, [selectedClient]: "loading" }));
    void fetchClientHealth(selectedClient)
      .then((health) => {
        setData((current) =>
          current
            ? {
                ...current,
                health: { ...current.health, [selectedClient]: health },
              }
            : current,
        );
        setHealthLoadStates((current) => ({ ...current, [selectedClient]: "ready" }));
      })
      .catch(() => {
        setHealthLoadStates((current) => ({ ...current, [selectedClient]: "error" }));
      });
  }, [data, healthLoadStates, selectedClient, session]);

  const setSelectedClient = (slug: string) => {
    if (session?.isClientUser && session.tenantSlug) {
      return;
    }
    setSelectedClientState(slug);
    localStorage.setItem(STORAGE_KEY, slug);
  };

  const selectedClientHealthState: ClientHealthLoadState = selectedClient === "ALL"
    ? "idle"
    : data?.health[selectedClient]
      ? "ready"
      : healthLoadStates[selectedClient] ?? "loading";

  const value = useMemo(
    () => ({
      data,
      session,
      selectedClient,
      setSelectedClient,
      loading,
      sessionReady: session !== null || !loading,
      selectedClientHealthState,
      error,
      refresh,
    }),
    [data, session, selectedClient, loading, selectedClientHealthState, error],
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

export function useDashboard(): DashboardContextValue {
  const context = useContext(DashboardContext);
  if (!context) throw new Error("useDashboard debe usarse dentro de DashboardProvider");
  return context;
}
